import { action, computed, makeObservable } from 'mobx';

import { concatPath, dev, flattenError, localTime, moment, TimeFormat } from '@feathr/hooks';
import type { Attributes, TConstraints } from '@feathr/rachis';

import { CAMPAIGN_DEFAULT_MAX_BUDGET } from '../constants';
import { REGEX_INPUT_VALIDATION } from '../global';
import { ReportModel } from '../model';
import type { Campaigns } from './campaigns';
import type { ICampaignAttributes } from './types';
import { CampaignClass, CampaignState } from './types';

const {
  Affinity,
  AutoPinpointEmail,
  Conversation,
  Drip,
  DripStep,
  EmailList,
  EmailListFacebook,
  Facebook,
  LandingPage,
  Lookalike,
  MailrailEmail,
  MobileGeoFenceRetargeting,
  MobileGeoFencing,
  PinpointEmail,
  Referral,
  Search,
  SeedSegment,
  Segment,
  SmartPinpointEmail,
  PinpointPartnerMessage,
  TrackedLink,
  GoogleAdsSmart,
  Omni,
} = CampaignClass;

const { Draft, Erroring, Published, Publishing, Stopped } = CampaignState;

export const CampaignLabelMap = new Map<CampaignClass, string>([
  [Affinity, 'Affinity'],
  [AutoPinpointEmail, 'Auto Send'],
  [Conversation, 'Conversation'],
  [Drip, 'Drip'],
  [DripStep, 'Drip Step'],
  [EmailList, 'Email Mapping'],
  [EmailListFacebook, 'Meta Email Mapping'],
  [Facebook, 'Meta Retargeting'],
  [GoogleAdsSmart, 'Google Ad Grants'],
  [LandingPage, 'Landing Page'],
  [Lookalike, 'Lookalike'],
  [MailrailEmail, 'Mailrail Email'],
  [MobileGeoFenceRetargeting, 'Historical Geofencing'],
  [MobileGeoFencing, 'Mobile Geofencing'],
  [Omni, 'Pixl Plus'],
  [PinpointEmail, 'Single Send'],
  [Referral, 'Invites'],
  [Search, 'Search Keyword'],
  [SeedSegment, 'Lookalike'],
  [Segment, 'Retargeting'],
  [SmartPinpointEmail, 'Smart Send'],
  [TrackedLink, 'Tracked Link'],
]);

export const leadTimeCampaigns = [MobileGeoFenceRetargeting, MobileGeoFencing, Search, SeedSegment];

/** Campaign classes that support TTD (Trade Desk) integration */
const TTD_CAMPAIGN_CLASSES = [
  Affinity,
  EmailList,
  Lookalike,
  MobileGeoFenceRetargeting,
  MobileGeoFencing,
  Search,
  SeedSegment,
  Segment,
  Omni,
];

export function getMaxTargetValue(
  maxBudget = CAMPAIGN_DEFAULT_MAX_BUDGET,
  isMonetization = false,
): number {
  if (!isMonetization) {
    return maxBudget;
  }

  /*
   * Budgets must be less than the max budget or the equivalent number of impressions
   * at a $5CPM for a Monetization campaign.The equivalent number of
   * impressions is $5 / 1000 imps.
   * $10,000 * 1000 imp / $5 = 2,000,000 impressions.
   */
  return (maxBudget * 1000) / 5;
}

export function getMinDaysAdvance(attributes: { cls: CampaignClass; type?: string }): number {
  const { cls } = attributes;

  if (leadTimeCampaigns.includes(cls)) {
    return 5;
  } else if ([Referral, LandingPage].includes(cls)) {
    return 0;
  }
  return 1;
}

export function getMinStartDate(attributes: { cls: CampaignClass; type?: string }): moment.Moment {
  const { cls, type } = attributes;

  const minDaysAdvance = getMinDaysAdvance({ cls, type });
  return moment.utc().add(minDaysAdvance, 'days').endOf('day');
}

export function getMinDuration(): moment.Duration {
  return moment.duration(3, 'days');
}

export const baseConstraints: TConstraints<ICampaignAttributes> = {
  date_start: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    datetime: (value: string, attributes: any) => {
      if (
        [
          LandingPage,
          MailrailEmail,
          PinpointEmail,
          SmartPinpointEmail,
          AutoPinpointEmail,
          Drip,
          DripStep,
        ].includes(attributes._cls)
      ) {
        return undefined;
      }
      if (attributes.state === Draft) {
        const minDaysAdvance = getMinDaysAdvance({
          cls: attributes._cls,
          type: attributes.email_mapping_type,
        });
        return {
          earliest: moment.utc().add(minDaysAdvance, 'days').format(TimeFormat.isoDate),
          message: `^Campaigns of this type must be made at least ${minDaysAdvance} day${
            minDaysAdvance > 1 ? 's' : ''
          } in advance.`,
        };
      }
      return true;
    },
    presence: (...args: any[]) => {
      const attributes = args[1];
      if (attributes._cls === LandingPage) {
        return undefined;
      }
      return {
        allowEmpty: false,
        message: '^Start date cannot be empty.',
      };
    },
  },
  date_end: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    datetime: (value: string, attributes: any) => {
      if (attributes._cls === LandingPage) {
        return undefined;
      }
      if (attributes.date_start) {
        const dateStart = moment.utc(attributes.date_start, TimeFormat.isoDateTime);
        const dateEnd = moment.utc(value, TimeFormat.isoDateTime);
        const minDuration = getMinDuration();

        if (dateEnd.isBefore(dateStart)) {
          return {
            earliest: dateStart.add(minDuration).format(TimeFormat.isoDateTime),
            message: '^End date must be after start date.',
          };
        } else {
          return {
            earliest: dateStart.add(minDuration).format(TimeFormat.isoDateTime),
            message: `Campaigns of this type must be at least ${minDuration.asDays()} days long.`,
          };
        }
      }

      return true;
    },
    presence: (_, attributes) => {
      if (attributes._cls === LandingPage) {
        return undefined;
      }
      return {
        allowEmpty: false,
        message: '^End date cannot be empty.',
      };
    },
  },
  state: {
    inclusion: {
      within: [Draft, Erroring, Published, Publishing, Stopped],
      message: '^The campaign is not in a valid state.',
    },
  },
  name: {
    exclusion: {
      within: ['New Draft Campaign'],
      message: '^Change the default name for this campaign.',
    },
    presence: {
      allowEmpty: false,
    },
    format: {
      pattern: REGEX_INPUT_VALIDATION,
      flags: 'i',
      message: '^Campaign names must consist of letters, numbers, and basic symbols.',
    },
  },
};

export abstract class Campaign<
  IAttributes extends ICampaignAttributes = ICampaignAttributes,
> extends ReportModel<IAttributes> {
  public reportKey = 'c' as const;
  public className = 'campaign' as const;
  public override collection: Campaigns<this> | null = null;

  public get constraints(): TConstraints<IAttributes> {
    return baseConstraints;
  }

  constructor(attributes: Partial<IAttributes> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public override set(patch: Partial<Attributes<this>>, dirty = true): void {
    super.set(patch, dirty);
  }

  public getItemUrl(pathSuffix?: string): string {
    const localPathSuffix = pathSuffix ?? (this.get('_cls') === Conversation ? 'edit' : undefined);

    const flightId = this.get('flight');
    if (flightId) {
      return concatPath(
        `/projects/${this.get('event')}/flights/${flightId}/campaigns/${this.id}`,
        localPathSuffix,
      );
    }
    // Drip Step campaigns should route to the Drip campaign page
    const id = this.get('_cls') === DripStep ? this.get('drip_campaign') : this.id;

    return concatPath(`/projects/${this.get('event')}/campaigns/${id}`, localPathSuffix);
  }

  // Clone a campaign using the Campaigns collection custom cloneCampaign method
  public cloneCampaign(data: Partial<IAttributes>): Promise<void> {
    this.checkReplaced();
    this.assertCollection(this.collection);

    if (this.id) {
      const conversionTrackingMode = this.get('conversion_tracking_mode');
      const cloneData = {
        ...data,
        conversion_tracking_mode: conversionTrackingMode,
        ...(conversionTrackingMode === 'advanced' && {
          conversion_goals: this.get('conversion_goals'),
        }),
      } as Partial<Attributes<this>>;

      return this.collection.cloneCampaign(this.id, cloneData);
    }

    throw new Error('Models need to have ids to clone');
  }

  public static readonly defaults: Partial<ICampaignAttributes> = {
    // Defaults match what gets auto added on the backend
    date_created: moment.utc().format(TimeFormat.isoDateTime),
    date_start: moment.utc().format(TimeFormat.isoDateTime),
    date_last_modified: moment.utc().format(TimeFormat.isoDateTime),
    exposure_settings: {
      target_cap_daily: 0,
      custom_target: false,
      recommended_target_percent: undefined,
      target_type: 'fixed_budget',
      target_value: undefined,
      pace: 'pace_to_end_of_flight',
    },
    is_enabled: true,
    name: '',
    parent_kind: 'event',
    rerun: false,
    state: CampaignState.Draft,
  };

  public override getDefaults(): Partial<IAttributes> {
    return {
      ...super.getDefaults(),
      ...Campaign.defaults,
    } as Partial<IAttributes>;
  }

  @action.bound
  public async publish(validate = true): Promise<Campaign> {
    if (!this.collection) {
      throw new Error('Campaign is not in a collection.');
    }
    if (validate && !this.isValid(['_cls', ...Object.keys(this.constraints)], false)) {
      if (dev.isActive) {
        const errors = this.validate([], false, 'grouped').errors;
        const flattenedErrors = Object.entries(errors).reduce<string[]>((acc, [key, error]) => {
          const keyErrors = flattenError(error);
          return keyErrors.length ? [...acc, `${key}: ${keyErrors.join('; ')}`] : acc;
        }, []);

        dev.error(`${this.constructor.name} is invalid`, flattenedErrors.join('\n'));
      }
      throw new Error('Campaign is invalid');
    }
    return this.collection.publish(this);
  }

  public async stop(): Promise<Campaign> {
    const patch = {
      state: this.get('_cls') === PinpointPartnerMessage ? Draft : Stopped,
    } as Partial<Attributes<this>>;
    this.set(patch);
    return this.patch(patch);
  }

  public override get reportRangeStart(): string {
    /*
     * date_send_start has a timestamp for the exact time emails should go out,
     * whereas date_start is generally only used as a representation for the date itself,
     * removed from the time
     */
    const dateStart = this.isEmail ? this.get('date_send_start') : this.get('date_start');
    const dateCreated = this.get('date_created');
    if (dateStart) {
      return dateStart;
    }
    if (dateCreated) {
      return dateCreated;
    }
    return moment.utc().format(TimeFormat.isoDate);
  }

  @computed
  public get isPublished(): boolean {
    return this.get('state') === Published;
  }

  @computed
  public get isPublishing(): boolean {
    return this.get('state') === Publishing;
  }

  @computed
  public get isStopped(): boolean {
    return this.get('state') === Stopped;
  }

  @computed
  public get isDraft(): boolean {
    return this.get('state') === Draft;
  }

  @computed
  public get isDisabledCampaignState(): boolean {
    return this.get('state') === Published || this.get('state') === Stopped;
  }

  @computed
  public get localStartTime(): string {
    return localTime(this.get('date_start'));
  }

  @computed
  public get localEndTime(): string {
    return localTime(this.get('date_end'));
  }

  @computed
  public get isCreativesCampaign(): boolean {
    return [...TTD_CAMPAIGN_CLASSES, EmailListFacebook, Facebook].includes(this.get('_cls'));
  }

  @computed
  public get isGeofencingCampaign(): boolean {
    return [MobileGeoFenceRetargeting, MobileGeoFencing].includes(this.get('_cls'));
  }

  @computed
  public get isEmailMappingCampaign(): boolean {
    return [EmailList, EmailListFacebook].includes(this.get('_cls'));
  }

  @computed
  public get isAwarenessCampaign(): boolean {
    return [Affinity, Search, Lookalike, MobileGeoFenceRetargeting, MobileGeoFencing].includes(
      this.get('_cls'),
    );
  }

  /**
   * Getter that returns whether or not the campaign is an ad campaign.
   * Uses campaign.isCreativesCampaign, but is written in this way
   * for clarity in other parts of the app.
   */
  @computed
  public get isAdCampaign(): boolean {
    return this.isCreativesCampaign;
  }

  @computed
  public get isTTDCampaign(): boolean {
    return TTD_CAMPAIGN_CLASSES.includes(this.get('_cls'));
  }

  @computed
  public get isSegmentCampaign(): boolean {
    return [Segment, Facebook, Omni].includes(this.get('_cls'));
  }

  @computed
  public get isRetargetingCampaign(): boolean {
    return [Segment, Facebook].includes(this.get('_cls'));
  }

  @computed
  public get isOmniCampaign(): boolean {
    return [Omni].includes(this.get('_cls'));
  }

  @computed
  public get isOmniReachCampaign(): boolean {
    return this.isOmniCampaign && this.get('objective') === 'reach';
  }

  @computed
  public get isLandingPageCampaign(): boolean {
    return [LandingPage].includes(this.get('_cls'));
  }

  @computed
  public get isAffinityCampaign(): boolean {
    return [Affinity].includes(this.get('_cls'));
  }

  @computed
  public get isConversationCampaign(): boolean {
    return [Conversation].includes(this.get('_cls'));
  }

  @computed
  public override get reportRangeEnd(): string {
    const dateEnd = this.isEmail ? this.get('date_send_end') : this.get('date_end');
    const momentEnd = moment.utc(dateEnd);
    const stats = this.get('stats_cache');
    const isConverting = stats?.conversions?.full?.num || 0;
    if (dateEnd && isConverting) {
      return momentEnd.add(30, 'days').format(TimeFormat.isoDate);
    }
    if (dateEnd) {
      return dateEnd;
    }
    return moment.utc().format(TimeFormat.isoDate);
  }

  @computed
  public get name(): string {
    const label = CampaignLabelMap.get(this.get('_cls'));
    return (
      this.get('name', '' as Exclude<IAttributes['name'], undefined>).trim() ||
      `Unnamed ${label ? label + ' ' : ''}Campaign`
    );
  }

  @computed
  public get isMonetization(): boolean {
    return (
      this.get('parent_kind') === 'partner' &&
      this.get('exposure_settings').target_type === 'fixed_impressions'
    );
  }

  @computed
  public get isFacebook(): boolean {
    return [Facebook, EmailListFacebook].includes(this.get('_cls'));
  }

  @computed
  public get isGoogle(): boolean {
    return this.get('_cls') === GoogleAdsSmart;
  }

  @computed
  public get isSingleSend(): boolean {
    return this.get('_cls') === PinpointEmail;
  }

  @computed
  public get isDrip(): boolean {
    return this.get('_cls') === Drip;
  }

  @computed
  public get isDonation(): boolean {
    return this.get('is_donation');
  }

  @computed
  public get isEmail(): boolean {
    return [PinpointEmail, SmartPinpointEmail, AutoPinpointEmail, Drip, DripStep].includes(
      this.get('_cls'),
    );
  }

  /**
   * Getter that checks if a campaign was created after the date
   * scripted updates was merged in. Campaigns that were live before
   * and after the merge may have statistical discrepancies across
   * report components due to changes in calculation methodology.
   */
  @computed
  public get isCreatedAfterScriptedUpdates(): boolean {
    return moment(this.get('date_start')).isAfter(moment('02-21-2023', 'MM-DD-YYYY'));
  }

  public isAfterDateSendStart({
    now = moment.utc(),
    offset = 0,
    offsetUnit = 'seconds',
  }: {
    now?: moment.Moment;
    offset?: number;
    offsetUnit?: moment.unitOfTime.DurationConstructor;
  } = {}): boolean {
    const sendStart = moment.utc(this.get('date_send_start'), moment.ISO_8601);
    return now.isSameOrAfter(sendStart.add(offset, offsetUnit));
  }

  /**
   * Getter that checks if an email campaign is past its end date
   * We offset 1 hour by default to allow time for the num_targeted stat
   * to finish processing before we display it.
   */
  public get isAfterDateSendEnd(): boolean {
    const now = moment.utc();
    const sendEnd = this.get('date_send_end');
    if (!sendEnd) {
      return false;
    }
    const sendEndMoment = moment.utc(sendEnd, moment.ISO_8601);
    return now.isSameOrAfter(sendEndMoment.add(1, 'hour'));
  }

  /**
   * Getter that checks if a non-email campaign is past its end date.
   */
  public get isAfterDateEnd(): boolean {
    const now = moment.utc();
    const end = this.get('date_end');
    if (!end) {
      return false;
    }
    return now.isSameOrAfter(end);
  }

  /**
   * Getter that checks if a non-email campaign is past its start date
   */
  public get isAfterDateStart(): boolean {
    const now = moment.utc();
    const start = this.get('date_start');
    if (!start) {
      return false;
    }
    return now.isSameOrAfter(start);
  }

  /**
   * Getter that checks if any campaign type is past its end date.
   */
  public get isPastEndDate(): boolean {
    return this.isEmail ? this.isAfterDateSendEnd : this.isAfterDateEnd;
  }

  public get isPastStartDate(): boolean {
    return this.isEmail ? this.isAfterDateSendStart() : this.isAfterDateStart;
  }
}

export class BaseCampaign extends Campaign<ICampaignAttributes> {}
